import { SAMPLE_FIGURES, usd } from './sample-figures';

export interface FeatureStep {
  title: string;
  description: string;
}

export interface FeaturePageData {
  slug: string;
  title: string;
  subtitle: string;
  seo: { title: string; description: string };
  problem: string;
  steps: FeatureStep[];
  capabilities: string[];
  relatedFeatures: string[];
}

export const FEATURES: FeaturePageData[] = [
  {
    slug: 'rent-collection',
    title: 'Rent Tracking with Zelle',
    subtitle: 'Tenants keep paying by Zelle, Venmo, or bank transfer. Resida spots each payment in your bank feed and matches it to the right lease.',
    seo: {
      title: 'Track Zelle Rent Payments',
      description: 'Track rent paid by Zelle, Venmo, Cash App, or bank transfer. Resida links your bank, matches each payment to the right tenant, flags late rent, and offers ACH for landlords with 1 to 20 units.',
    },
    problem:
      'Most small landlords already get rent by Zelle or Venmo, and most rent apps ignore it. You end up scrolling your bank app to see who paid, matching names by hand, and keeping a spreadsheet so you know who is late.',
    steps: [
      {
        title: 'Link your bank',
        description: 'Connect the account your rent lands in through Plaid. Resida only reads that account\'s transactions.',
      },
      {
        title: 'Tell Resida who is who, once',
        description: 'Match each tenant\'s first payment by hand. Resida remembers the names their bank uses and matches future payments on its own.',
      },
      {
        title: 'Know who paid and who is late',
        description: 'Payments land on the right lease automatically. Late rent is flagged, late fees are calculated from your lease terms, and reminders go out with your Zelle details.',
      },
      {
        title: 'Offer ACH when you want it',
        description: 'Tenants can also pay by ACH bank transfer through their portal, tracked in the same ledger.',
      },
    ],
    capabilities: [
      'Zelle, Venmo, Cash App, and bank transfer detection',
      'Bank linking through Plaid (read-only)',
      'Learns each tenant\'s payment names and matches automatically',
      'Late rent flags and late-fee calculation from your lease',
      'Reminders that include your Zelle payment details',
      'ACH payments through the tenant portal',
      'Partial payment tracking',
      'Full payment history per tenant',
    ],
    relatedFeatures: ['ai-assistants', 'lease-management', 'expense-tracking'],
  },
  {
    slug: 'lease-management',
    title: 'Lease Management',
    subtitle: 'Create leases with a guided wizard that reminds you of the clauses and disclosures your state and city call for.',
    seo: {
      title: 'Lease Management Software',
      description: 'Create rental leases with a guided wizard. State- and city-specific clause and disclosure reminders, renewal tracking, and built-in e-signatures for landlords with 1 to 20 units.',
    },
    problem:
      'Lease requirements vary by state and city, and a generic template can miss a required disclosure. You end up emailing Word docs back and forth, the signed version gets buried in your inbox, and the renewal date sneaks up on you. Resida reminds you what to include; it does not give legal advice.',
    steps: [
      {
        title: 'Start the lease wizard',
        description: 'Select your property and unit. The wizard loads the clause and disclosure reminders for that unit\'s state and city.',
      },
      {
        title: 'Fill in the terms',
        description: 'Walk through rent amount, deposit, pet policies, rules, and required disclosures step by step. Nothing gets missed.',
      },
      {
        title: 'Review and send',
        description: 'Preview the complete lease, then send it to your tenant for e-signature directly from Resida.',
      },
      {
        title: 'Store and manage',
        description: 'Signed leases are stored with the unit. Renewal dates show up in your calendar automatically.',
      },
    ],
    capabilities: [
      '9-step guided lease creation wizard',
      'State- and city-specific clause reminders',
      'Required disclosure reminders',
      'Pet policy configuration',
      'Rules and regulations templates',
      'Lease renewal tracking with calendar reminders',
      'Document storage per unit',
      'Tenant onboarding from the lease',
    ],
    relatedFeatures: ['document-signing', 'walkthroughs', 'rent-collection'],
  },
  {
    slug: 'expense-tracking',
    title: 'Expense Tracking by Email',
    subtitle: 'Forward a receipt or invoice to your Resida address. AI files it under the right property and unit, keeps the original, and assigns the IRS Schedule E category.',
    seo: {
      title: 'Rental Property Expense Tracking',
      description: 'Forward rental receipts and invoices by email. Resida extracts the details, matches each expense to the right property and unit, stores the original receipt, and categorizes it for IRS Schedule E.',
    },
    problem:
      'Rental receipts arrive everywhere: contractor emails, store receipts, utility bills, invoices from your handyman. Sorting out which property each one belongs to, and finding the original at tax time, takes hours. Most landlord tools make you type every expense in by hand.',
    steps: [
      {
        title: 'Forward it',
        description: 'Every landlord gets a dedicated Resida email address. Forward a receipt, invoice, or bill to it, or upload a photo in the app.',
      },
      {
        title: 'AI reads it and finds the unit',
        description: 'Resida extracts the vendor, amount, and date, and matches the expense to the right property and unit using your addresses, recent repair messages, and the vendors you have used before.',
      },
      {
        title: 'The original is kept',
        description: 'The source email and every attachment are stored with the expense, so the real receipt is one click away when your accountant asks.',
      },
      {
        title: 'Confident ones file themselves',
        description: 'When the AI is sure, the expense is filed automatically. When it is not, it waits in a short review queue for you to confirm.',
      },
      {
        title: 'Tax-ready at year end',
        description: 'Every expense carries its IRS Schedule E category. Export reports by property and category for your CPA, or file yourself.',
      },
    ],
    capabilities: [
      'Dedicated email address for receipts and invoices',
      'Automatic matching to the right property and unit',
      'Original emails and attachments stored with each expense',
      'Learns which vendors go with which property',
      'Auto-filing when confident, review queue when not',
      'AI receipt scanning from photos and uploads',
      'IRS Schedule E categorization',
      'Recurring expenses and project groupings',
      'Tax-ready reports by property and category',
    ],
    relatedFeatures: ['maintenance', 'ai-assistants', 'dashboard'],
  },
  {
    slug: 'maintenance',
    title: 'Maintenance & Repairs',
    subtitle: 'Structured repair requests with photos and status tracking, plus an optional AI coordinator that works with your vendors.',
    seo: {
      title: 'Rental Maintenance Tracking',
      description: 'Track maintenance requests from submission to completion. Structured requests with photos, your vendor list, and an optional AI repair coordinator for landlords.',
    },
    problem:
      'Maintenance requests come in as vague text messages at 11pm. You lose track of what was reported, what you promised, and whether it got fixed.',
    steps: [
      {
        title: 'Tenant submits a request',
        description: 'Tenants describe the issue through their portal with a structured form and photo uploads. No more "hey the thing is broken" texts.',
      },
      {
        title: 'You review and prioritize',
        description: 'Requests appear in your dashboard with photos, descriptions, and the affected unit. Triage by urgency.',
      },
      {
        title: 'Track to completion',
        description: 'Update status as work progresses. Both you and the tenant can see where things stand at any time.',
      },
      {
        title: 'Let AI coordinate (optional)',
        description: 'Turn on the repair coordinator and it triages new requests, contacts a vendor from your list, and schedules the visit. It keeps the tenant updated and brings you the decisions that need you.',
      },
    ],
    capabilities: [
      'Structured request forms with photo uploads',
      'Status tracking (submitted, in progress, completed)',
      'Your list of trusted vendors',
      'Optional AI repair coordinator: triage, vendor outreach, scheduling',
      'Tenant visibility into request status',
      'Priority and urgency indicators',
    ],
    relatedFeatures: ['walkthroughs', 'tenant-communication', 'dashboard'],
  },
  {
    slug: 'tenant-communication',
    title: 'Tenant Communication',
    subtitle: 'In-app chat and email in one inbox, organized by property and tenant.',
    seo: {
      title: 'Landlord-Tenant Communication',
      description: 'Communicate with tenants through in-app chat and email from a single inbox. Organized by property, with full message history.',
    },
    problem:
      'Tenant communication is scattered across your personal text messages, email, and maybe a group chat. You can\'t find the message where you agreed to something three months ago. Important requests get lost in the noise of your personal inbox.',
    steps: [
      {
        title: 'Tenants message through their portal',
        description: 'Tenants send messages from their Resida portal. Everything stays organized and out of your personal phone.',
      },
      {
        title: 'You respond from one inbox',
        description: 'All conversations — in-app chat and email — appear in a single inbox organized by property and tenant.',
      },
      {
        title: 'Full history, always available',
        description: 'Every message is stored and searchable. When you need to recall what was discussed, it\'s all there.',
      },
    ],
    capabilities: [
      'In-app chat with tenants',
      'Email integration in one inbox',
      'Conversations organized by property and tenant',
      'Full message history and search',
      'Attachment support',
      'Read receipts',
      'Multi-party conversations',
    ],
    relatedFeatures: ['maintenance', 'lease-management', 'rent-collection'],
  },
  {
    slug: 'document-signing',
    title: 'Document Signing',
    subtitle: 'Built-in e-signatures with no third-party fees. Send, sign, and store — all in Resida.',
    seo: {
      title: 'E-Signatures for Landlords',
      description: 'Built-in electronic signatures for rental documents. No third-party fees. Send leases for signing and store executed copies automatically.',
    },
    problem:
      `E-signature services charge ${usd(SAMPLE_FIGURES.esignLow)} to ${usd(SAMPLE_FIGURES.esignHigh)} per document. When you're sending leases, addendums, and disclosures, those fees add up fast. Or you skip e-signatures entirely and deal with printing, scanning, and mailing.`,
    steps: [
      {
        title: 'Create your document',
        description: 'Build a lease or upload a document. Resida handles the formatting and signature placement.',
      },
      {
        title: 'Send for signing',
        description: 'Send directly to your tenant via their portal. They receive a notification and can sign from any device.',
      },
      {
        title: 'Tenant signs electronically',
        description: 'Tenants review and sign through a secure signing page. No account required, no app to download.',
      },
      {
        title: 'Signed copy stored automatically',
        description: 'The executed document is stored with the unit record. Both parties can access it anytime.',
      },
    ],
    capabilities: [
      'E-signatures built in, with no per-signature fees',
      'Signing envelope tracking and status',
      'Public signing links for tenants',
      'Automatic storage of signed documents',
      'Works on any device (phone, tablet, computer)',
      'No tenant account or app required to sign',
    ],
    relatedFeatures: ['lease-management', 'dashboard', 'tenant-communication'],
  },
  {
    slug: 'dashboard',
    title: 'Attention Dashboard',
    subtitle: 'One list of everything that needs you, across every unit, sorted by urgency and cleared as you handle it.',
    seo: {
      title: 'Landlord Dashboard & To-Do List',
      description: 'One prioritized list of everything that needs a landlord\'s attention: late rent, leases to renew, documents to sign, repair requests, unanswered leads, and receipts to review. For landlords with 1 to 20 units.',
    },
    problem:
      'Running rentals means watching a dozen things at once: who paid, whose lease is ending, which repair is waiting, which prospect never got a reply. Miss one and it turns into a vacancy, a late fee you forgot to charge, or an unhappy tenant.',
    steps: [
      {
        title: 'Everything lands in one list',
        description: 'Past-due rent, late fees to approve, leases to renew, documents awaiting signature, repair requests, unanswered leads and messages, applications, walkthroughs, and receipts to review all show up in one place.',
      },
      {
        title: 'Sorted by urgency',
        description: 'Overdue and time-sensitive items sit at the top, so you always know what to do first.',
      },
      {
        title: 'Handle it and it clears',
        description: 'Each item links straight to where you deal with it. Once it is done, it drops off the list on its own; there is nothing to check off.',
      },
      {
        title: 'Tenants get one too',
        description: 'Tenants see their own list in the portal: rent due, documents to sign, move-in tasks, and messages waiting for them.',
      },
    ],
    capabilities: [
      'One list across every property and unit',
      'Sorted by urgency',
      'Rent, leases, signatures, repairs, leads, and receipts',
      'Links straight to the task',
      'Items clear themselves when handled',
      'A matching to-do list for tenants',
      'Available to Claude and ChatGPT through the Resida connector',
    ],
    relatedFeatures: ['rent-collection', 'leasing', 'ai-assistants'],
  },
  {
    slug: 'leasing',
    title: 'Leasing & Listings',
    subtitle: 'Publish a listing once and follow every lead from first message to signed lease.',
    seo: {
      title: 'Rental Listing & Leasing Software',
      description: 'Publish rental listings, fill Craigslist and Facebook Marketplace posts with one click, and track every lead, showing, and application in one pipeline. For landlords with 1 to 20 units.',
    },
    problem:
      'A vacancy means posting the same listing on several sites, then answering the same questions over and over by text, email, and Marketplace message. Showings get double-booked, applications live in your inbox, and good prospects slip away while you\'re at work.',
    steps: [
      {
        title: 'Create the listing',
        description: 'Write the listing once in Resida: photos, rent, availability, and the details prospects ask about.',
      },
      {
        title: 'Post it where renters search',
        description: 'The Resida browser extension fills in your Craigslist and Facebook Marketplace posts from the same details, so you only type them once.',
      },
      {
        title: 'Track every lead',
        description: 'Inquiries land in one pipeline. Send prospects a link to pick a showing time, review applications, and see where each lead stands.',
      },
      {
        title: 'Let AI answer first (optional)',
        description: 'Turn on the leasing assistant and it answers prospect questions from your listing and sends scheduling links. Anything it can\'t answer comes to you.',
      },
    ],
    capabilities: [
      'Write the listing once, post it on the sites you choose',
      'Browser extension for Craigslist and Facebook Marketplace',
      'Lead pipeline from inquiry to signed lease',
      'Self-service showing scheduling links',
      'Online rental applications',
      'Reminders for leads waiting on a reply',
      'Optional AI leasing assistant for prospect questions',
    ],
    relatedFeatures: ['lease-management', 'walkthroughs', 'dashboard'],
  },
  {
    slug: 'walkthroughs',
    title: 'Move-In & Move-Out Walkthroughs',
    subtitle: 'Room-by-room condition checklists with photos, compared side by side when the tenant moves out.',
    seo: {
      title: 'Move-In & Move-Out Inspection Checklist',
      description: 'Digital move-in and move-out condition checklists with photos for landlords. Reusable room-by-room templates, tenant walkthroughs by link, and a side-by-side deduction report.',
    },
    problem:
      'When a tenant moves out, the security deposit conversation comes down to what the unit looked like on move-in day. Without photos and a checklist both sides saw, it\'s your memory against theirs.',
    steps: [
      {
        title: 'Set up your checklist',
        description: 'Start from a room-by-room template and adjust it to the unit. Reuse it for every turnover.',
      },
      {
        title: 'Walk the unit at move-in',
        description: 'Record each item\'s condition, with photos of anything damaged. Send the tenant a link so they can do their own walkthrough from their phone.',
      },
      {
        title: 'Repeat at move-out',
        description: 'Run the same checklist when the tenant leaves, so both records cover the same items.',
      },
      {
        title: 'Build the deduction report',
        description: 'See move-in and move-out side by side for each item, add amounts and receipts, and send the report to your tenant.',
      },
    ],
    capabilities: [
      'Room-by-room condition checklists',
      'Photos for every damaged item',
      'Reusable checklist templates',
      'Tenant walkthrough by link, no account needed',
      'Side-by-side move-in and move-out comparison',
      'Deduction report with amounts and receipts',
      'Reminders when a walkthrough is pending',
    ],
    relatedFeatures: ['lease-management', 'document-signing', 'maintenance'],
  },
  {
    slug: 'ai-assistants',
    title: 'Your Rentals in Claude and ChatGPT',
    subtitle: 'Connect Claude or ChatGPT to Resida and ask about your rentals in plain English, or let it handle the small tasks you allow.',
    seo: {
      title: 'Claude & ChatGPT for Landlords',
      description: 'Connect Claude, ChatGPT, or another AI assistant to your Resida account. Ask who is behind on rent, what you spent on a property, or what needs attention, and approve routine actions in conversation.',
    },
    problem:
      'Answering a simple question about your rentals means opening an app, finding the right screen, and adding things up yourself. Meanwhile you already ask an AI assistant about everything else.',
    steps: [
      {
        title: 'Connect your assistant',
        description: 'Add Resida as a connector in Claude or ChatGPT (or another assistant that supports MCP) and sign in. You choose what it is allowed to see and do.',
      },
      {
        title: 'Ask in plain English',
        description: '"Who is behind on rent?" "What did I spend on repairs at Oak Street this year?" "What needs my attention today?" Answers come from your live Resida data.',
      },
      {
        title: 'Let it handle small tasks',
        description: 'Approve a pending late fee or match a bank payment to a tenant from the conversation, only if you have allowed those tools.',
      },
      {
        title: 'Stay in control',
        description: 'See every connected app, change its permissions, or disconnect it at any time from your settings.',
      },
    ],
    capabilities: [
      'Works with Claude, ChatGPT, and other MCP-compatible assistants',
      'Secure sign-in; your password is never shared',
      'Portfolio, tenant, lease, and payment questions',
      'Expense and financial summaries',
      'Overdue rent and pending task lists',
      'Approve late fees and match payments in conversation',
      'Per-tool permissions you control',
      'Disconnect any time',
    ],
    relatedFeatures: ['rent-collection', 'dashboard', 'expense-tracking'],
  },
];

export function getFeatureBySlug(slug: string): FeaturePageData | undefined {
  return FEATURES.find((f) => f.slug === slug);
}
